import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import { Button } from "@/components/ui/button";
import { useAgentChat } from "@/contexts/AgentChatContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useCurrentUserProfile } from "@/hooks/useCurrentUserProfile";
import {
  MANAGED_FLOATING_WIDGET_ATTRIBUTE,
  useFloatingWidgetSuppression,
} from "@/lib/floatingWidgetSuppression";
import {
  getConfiguredFrontChatId,
  identifyFrontChatUser,
  initFrontChat,
  setFrontChatVisibility,
  showFrontChatWindow,
  shutdownFrontChat,
} from "@/lib/frontChat";
import { FrontLauncherIcon } from "@/lib/FrontLauncherIcon";
import { getFrontChatUserHash, normalizeFrontChatEmail } from "@/lib/frontChatIdentity";
import { useManagedFloatingWidgetPlacement } from "@/lib/floatingWidgetLayout";
import { useWidgetPreferences } from "@/lib/widgetPreferences";

function getDisplayName(profile?: { first_name?: string | null; last_name?: string | null } | null) {
  const name = [profile?.first_name, profile?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
}

export function CustomerFeedbackBridge() {
  const { isMinimized, isOpen } = useAgentChat();
  const { currentOrganization, currentMembership, user } = useOrganization();
  const profile = useCurrentUserProfile(user?.id);
  const chatId = getConfiguredFrontChatId();
  const { preferences } = useWidgetPreferences();
  const shouldHideChat = isOpen && !isMinimized;
  const displayName = getDisplayName(profile);
  const supportChatButtonRef = useRef<HTMLButtonElement>(null);
  const supportChatPosition = useManagedFloatingWidgetPlacement("front-chat", ["front-chat"]);
  const authenticatedEmail = normalizeFrontChatEmail(user?.email);
  const [frontIdentityHash, setFrontIdentityHash] = useState<{
    identityKey: string;
    userHash: string | null;
  } | null>(null);
  const frontIdentityKey = user?.id && authenticatedEmail ? `${user.id}:${authenticatedEmail}` : null;
  const frontUserHash = frontIdentityHash?.identityKey === frontIdentityKey ? frontIdentityHash.userHash : null;
  const frontIdentity = useMemo(() => ({
    email: authenticatedEmail,
    userHash: frontUserHash,
    name: displayName,
    organizationName: currentOrganization?.name,
    role: currentMembership?.role,
  }), [authenticatedEmail, currentMembership?.role, currentOrganization?.name, displayName, frontUserHash]);
  const canRequestFrontChatIdentity = Boolean(chatId && preferences.front && user?.id && authenticatedEmail);
  const canUseFrontChat = Boolean(canRequestFrontChatIdentity && frontUserHash);
  const frontIdentityRef = useRef({
    ...frontIdentity,
  });

  useEffect(() => {
    frontIdentityRef.current = frontIdentity;
  }, [frontIdentity]);

  useEffect(() => {
    if (!user?.id) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username: displayName ?? user.email ?? undefined,
    });
  }, [displayName, user?.email, user?.id]);

  useEffect(() => {
    if (!canRequestFrontChatIdentity || !authenticatedEmail || !frontIdentityKey) {
      shutdownFrontChat({ clearSession: true });
      return;
    }

    let cancelled = false;
    const requestedIdentityKey = frontIdentityKey;

    void getFrontChatUserHash({ email: authenticatedEmail })
      .then((userHash) => {
        if (cancelled) {
          return;
        }

        if (!userHash) {
          shutdownFrontChat({ clearSession: true });
        }
        setFrontIdentityHash({ identityKey: requestedIdentityKey, userHash });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setFrontIdentityHash({ identityKey: requestedIdentityKey, userHash: null });
        shutdownFrontChat({ clearSession: true });
        Sentry.captureException(error, {
          tags: {
            integration: "front_chat",
            operation: "identity_hash",
          },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [authenticatedEmail, canRequestFrontChatIdentity, frontIdentityKey]);

  useEffect(() => {
    if (!canUseFrontChat || !chatId) {
      shutdownFrontChat({ clearSession: true });
      return;
    }

    const controller = new AbortController();

    void initFrontChat(chatId, {
      signal: controller.signal,
      identity: frontIdentityRef.current,
      identityKey: frontIdentityKey,
    })
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }
        identifyFrontChatUser({
          name: frontIdentityRef.current.name,
          organizationName: frontIdentityRef.current.organizationName,
          role: frontIdentityRef.current.role,
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        Sentry.captureException(error, {
          tags: {
            integration: "front_chat",
          },
        });
      });

    return () => {
      controller.abort();
      shutdownFrontChat();
    };
  }, [canUseFrontChat, chatId, frontIdentityKey]);

  useEffect(() => {
    if (!canUseFrontChat) {
      return;
    }

    identifyFrontChatUser({
      name: displayName,
      organizationName: currentOrganization?.name,
      role: currentMembership?.role,
    });
  }, [canUseFrontChat, currentMembership?.role, currentOrganization?.name, displayName]);

  useEffect(() => {
    if (shouldHideChat) {
      setFrontChatVisibility(false);
    }
  }, [shouldHideChat]);
  useFloatingWidgetSuppression(supportChatButtonRef, {
    enabled: Boolean(canUseFrontChat && !shouldHideChat),
  });

  if (!canUseFrontChat || shouldHideChat) {
    return null;
  }

  return (
    <Button
      ref={supportChatButtonRef}
      type="button"
      size="icon"
      className="pointer-events-auto fixed z-50 h-10 w-10 rounded-full bg-[#a785ff] text-white shadow-lg shadow-[#a785ff]/25 transition-transform hover:scale-105 hover:bg-[#9675f3]"
      style={{
        right: `${supportChatPosition.right}px`,
        bottom: `${supportChatPosition.bottom}px`,
      }}
      onClick={showFrontChatWindow}
      aria-label="Open Support chat"
      {...{ [MANAGED_FLOATING_WIDGET_ATTRIBUTE]: "front-chat" }}
    >
      <FrontLauncherIcon />
    </Button>
  );
}
